import Constant from "../util/constant";
import {
  formatClubEmployeeDisplayName,
  pickClubEmployeeEmpref,
} from "./clubEmployeeList";

const ISSUE_LABEL_KEYS = {
  sync_pending: "EVENT_SUMMARY_ISSUE_SYNC_PENDING",
  not_closed: "EVENT_SUMMARY_ISSUE_NOT_CLOSED",
};

const ISSUE_BADGE_CLASS = {
  sync_pending: "event-summary-issue-sync",
  not_closed: "event-summary-issue-not-closed",
};

/**
 * @param {string | null | undefined} eventstatus
 */
export function isEventStatusCompleted(eventstatus) {
  if (eventstatus == null || eventstatus === "") return false;
  const value = String(eventstatus).trim();
  return (
    value === Constant.GENERALKEYS.COMPELETED ||
    value.toLowerCase() === "completed"
  );
}

function pickFirstString(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function pickPendingDevicesList(data) {
  const keys = [
    "pendingDevices",
    "pending_devices",
    "devices",
    "scannerDevices",
    "scanner_devices",
    "pending",
  ];
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
}

function pickOperatorObject(device) {
  const candidate =
    device?.operator ??
    device?.Operator ??
    device?.deviceOperator ??
    device?.device_operator;
  return candidate && typeof candidate === "object" ? candidate : null;
}

function buildDeviceRegistrationLookup(data) {
  const lookup = new Map();
  const lists = [
    data?.deviceRegistrations,
    data?.device_registrations,
    data?.registrations,
    data?.registeredDevices,
    data?.registered_devices,
  ].filter(Array.isArray);

  for (const list of lists) {
    for (const reg of list) {
      const loginId = pickFirstString(reg?.loginId, reg?.login_id, reg?.loginid);
      const deviceId = pickFirstString(reg?.deviceId, reg?.device_id, reg?.deviceid);
      const operatorName = pickFirstString(
        reg?.operatorName,
        reg?.operator_name,
        reg?.name,
        reg?.displayName,
        reg?.display_name,
        reg?.label
      );
      if (!operatorName) continue;

      if (loginId && deviceId) {
        lookup.set(`${loginId}::${deviceId}`, operatorName);
      }
      if (deviceId) lookup.set(deviceId, operatorName);
      if (loginId) lookup.set(loginId, operatorName);
    }
  }

  return lookup;
}

/**
 * @param {unknown} device
 * @param {Map<string, string>} [registrationLookup]
 */
export function normalizePendingDevice(device, registrationLookup) {
  const registration =
    device?.deviceRegistration ??
    device?.device_registration ??
    device?.registration;
  const operator = pickOperatorObject(device);

  const loginId = pickFirstString(device?.loginId, device?.login_id, device?.loginid);
  const deviceId = pickFirstString(device?.deviceId, device?.device_id, device?.deviceid);
  const operatorName = pickFirstString(
    device?.operatorName,
    device?.operator_name,
    device?.operatorname,
    operator?.name,
    operator?.operatorName,
    operator?.operator_name,
    operator?.displayName,
    operator?.display_name,
    registration?.operatorName,
    registration?.operator_name,
    registration?.name,
    registration?.displayName,
    registration?.display_name,
    device?.displayName,
    device?.display_name,
    device?.deviceName,
    device?.device_name,
    device?.registeredName,
    device?.registered_name,
    device?.label,
    device?.title,
    registrationLookup?.get(`${loginId}::${deviceId}`),
    registrationLookup?.get(deviceId),
    registrationLookup?.get(loginId)
  );
  return {
    loginId,
    deviceId,
    operatorName,
    syncPending: Boolean(
      device?.syncPending ?? device?.sync_pending ?? device?.syncpending
    ),
    eventClosed: Boolean(
      device?.eventClosed ?? device?.event_closed ?? device?.eventclosed
    ),
    closedBy: device?.closedBy ?? device?.closed_by ?? null,
    overrideReason:
      device?.overrideReason ?? device?.override_reason ?? null,
    issues: Array.isArray(device?.issues) ? device.issues : [],
  };
}

/**
 * Match scanner login to club employee display name (empref / username / email).
 * @param {string | undefined} loginId
 * @param {unknown[]} employees
 */
export function resolveOperatorNameFromEmployees(loginId, employees) {
  const needle = String(loginId ?? "").trim().toLowerCase();
  if (!needle || !Array.isArray(employees) || employees.length === 0) {
    return "";
  }

  for (const row of employees) {
    const matchKeys = new Set();
    const add = (value) => {
      const text = String(value ?? "").trim().toLowerCase();
      if (text) matchKeys.add(text);
    };

    add(pickClubEmployeeEmpref(row));
    add(row?.username);
    add(row?.user?.username);
    add(row?.loginId);
    add(row?.login_id);
    add(row?.scannerLogin);
    add(row?.scanner_login);

    const email = String(row?.email ?? row?.user?.email ?? "").trim();
    if (email) {
      add(email);
      add(email.split("@")[0]);
    }

    if (!matchKeys.has(needle)) continue;

    const displayName = formatClubEmployeeDisplayName(row);
    if (displayName) return displayName;

    const empref = pickClubEmployeeEmpref(row);
    if (empref) return empref;
  }

  return "";
}

/**
 * Fill missing operatorName from club employee list when the API omits it.
 * @param {ReturnType<typeof normalizeEventSummaryReadiness>} readiness
 * @param {unknown[]} [employees]
 */
export function enrichEventSummaryReadiness(readiness, employees = []) {
  if (!readiness?.pendingDevices?.length) return readiness;

  const pendingDevices = readiness.pendingDevices.map((device) => {
    const existing = String(device?.operatorName ?? "").trim();
    if (existing) return device;

    const fromEmployee = resolveOperatorNameFromEmployees(
      device.loginId,
      employees
    );
    if (!fromEmployee) return device;

    return { ...device, operatorName: fromEmployee };
  });

  return { ...readiness, pendingDevices };
}

/**
 * @param {unknown} raw
 */
export function normalizeEventSummaryReadiness(raw) {
  const payload = raw?.data != null && typeof raw.data === "object" ? raw : raw;
  const data =
    payload?.data != null && typeof payload.data === "object"
      ? payload.data
      : payload;

  const registrationLookup = buildDeviceRegistrationLookup(data);

  return {
    canDownload: Boolean(data?.canDownload ?? data?.can_download),
    eventStatus: data?.eventStatus ?? data?.eventstatus ?? null,
    eventEnded: Boolean(data?.eventEnded ?? data?.event_ended),
    requiredDeviceCount: Number(
      data?.requiredDeviceCount ?? data?.required_device_count ?? 0
    ),
    readyDeviceCount: Number(
      data?.readyDeviceCount ?? data?.ready_device_count ?? 0
    ),
    pendingDevices: pickPendingDevicesList(data).map((device) =>
      normalizePendingDevice(device, registrationLookup)
    ),
  };
}

/**
 * @param {unknown} raw
 * @param {unknown[]} [employees]
 */
export function buildEventSummaryReadiness(raw, employees = []) {
  return enrichEventSummaryReadiness(
    normalizeEventSummaryReadiness(raw),
    employees
  );
}

/**
 * @param {string[] | undefined} issues
 * @param {(key: string) => string} t
 * @returns {{ code: string; label: string; badgeClass: string }[]}
 */
export function getDeviceIssueItems(issues, t) {
  if (!Array.isArray(issues) || issues.length === 0) {
    return [
      {
        code: "unknown",
        label: t("EVENT_SUMMARY_ISSUE_UNKNOWN"),
        badgeClass: "event-summary-issue-unknown",
      },
    ];
  }
  return issues.map((issue) => {
    const code = String(issue).trim();
    const key = ISSUE_LABEL_KEYS[code];
    return {
      code,
      label: key ? t(key) : t("EVENT_SUMMARY_ISSUE_UNKNOWN"),
      badgeClass: ISSUE_BADGE_CLASS[code] || "event-summary-issue-unknown",
    };
  });
}

/**
 * @param {string[] | undefined} issues
 * @param {(key: string) => string} t
 */
export function formatDeviceIssueLabels(issues, t) {
  return getDeviceIssueItems(issues, t)
    .map((item) => item.label)
    .join(", ");
}

/**
 * Short display for long device ids (full value in title attribute).
 * @param {string | undefined} deviceId
 */
export function formatDeviceIdDisplay(deviceId) {
  const id = String(deviceId ?? "").trim();
  if (!id) return "—";
  if (id.length <= 20) return id;
  return `${id.slice(0, 10)}…${id.slice(-6)}`;
}

/**
 * Primary list label: operator name when registered, else login or device id.
 * @param {{ operatorName?: string; loginId?: string; deviceId?: string }} device
 */
export function getPendingDevicePrimaryLabel(device) {
  const operatorName = String(device?.operatorName ?? "").trim();
  if (operatorName) return operatorName;

  const loginId = String(device?.loginId ?? "").trim();
  if (loginId) return loginId;

  const deviceId = String(device?.deviceId ?? "").trim();
  if (deviceId) return formatDeviceIdDisplay(deviceId);

  return "—";
}

/**
 * Secondary line: loginId · deviceId (device id truncated when long).
 * @param {{ loginId?: string; deviceId?: string }} device
 */
export function getPendingDeviceSecondaryLabel(device) {
  const loginId = String(device?.loginId ?? "").trim();
  const deviceId = String(device?.deviceId ?? "").trim();
  const parts = [];

  if (loginId) parts.push(loginId);
  if (deviceId) parts.push(formatDeviceIdDisplay(deviceId));

  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * @param {{ loginId?: string; deviceId?: string }} device
 */
export function pendingDeviceKey(device) {
  return `${String(device?.loginId ?? "")}::${String(device?.deviceId ?? "")}`;
}
